import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import styled from 'styled-components'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { ManagedBalanceLedger, WithdrawalAccount, WithdrawalDestination, WithdrawalRequest } from '../state/Radom'
import { getMethod, ManagedPaymentMethod } from '../util/Managed'
import DestinationDetails from '../components/DestinationDetails'
import WithdrawalDetails from '../components/WithdrawalDetails'
import { errorToast, roundAssetAmount, successToast, trimZeros } from '../util/Util'
import { Chevron } from '../icons/Chevron'
import USDLogo from '../images/USDLogo.svg'
import EUROLogo from '../images/EUROLogo.svg'
import { StatusBadge } from './CheckoutSessions'
import { IconButton } from '../components/Button'
import Copy from '../icons/Copy'
import { Section, SectionContent, SectionItem } from '../components/Shared'
import _ from 'lodash'

const SubTitle = styled.div`
  font-size: 14px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const Withdrawal = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [withdrawalEvent, setWithdrawalEvent] = useState<WithdrawalRequest>()
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [isLoading, setIsLoading] = useState(true)
  const isFiatWithdrawal = useMemo(() => !!withdrawalEvent?.receipt?.FiatReceipt, [withdrawalEvent])
  const [receiptAccount, setReceiptAccount] = useState<WithdrawalAccount>()
  const [withdrawalDestination, setWithdrawalDestination] = useState<WithdrawalDestination>()
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger>()

  const {
    accountName,
    accountNumber,
    routingNumber,
    accountId
  } = useMemo(() => {
    const accountName = receiptAccount?.name
    const accountNumber =
        receiptAccount?.accountDetails.Fiat?.accountType.us?.accountNumber ||
        receiptAccount?.accountDetails.Fiat?.accountType.iban?.iban
    const routingNumber =
        receiptAccount?.accountDetails.Fiat?.accountType.us?.routingNumber ||
        receiptAccount?.accountDetails.Fiat?.accountType.iban?.bic
    const accountId = receiptAccount?.id
    return {
      accountName,
      accountNumber,
      routingNumber,
      accountId
    }
  }, [receiptAccount])

  const {
    transactionHash,
    networkFeeAmount,
    fiat
  } = useMemo(() => {
    const evm = withdrawalEvent?.receipt?.EVMReceipt
    const dot = withdrawalEvent?.receipt?.DOTReceipt
    const btc = withdrawalEvent?.receipt?.BTCReceipt
    const sol = withdrawalEvent?.receipt?.SOLReceipt
    const fiat = withdrawalEvent?.receipt?.FiatReceipt

    const transactionHash =
    evm?.transactionHash ||
    dot?.transactionHash ||
    btc?.transactionId ||
    sol?.signature ||
    fiat?.transactionHash

    const networkFeeAmount =
    evm?.networkFeeAmount ||
    dot?.networkFeeAmount ||
    btc?.networkFeeAmount ||
    sol?.networkFeeAmount

    const currency: 'EUR' | 'USD' = !fiat?.transferMethod || fiat.transferMethod.toLowerCase() === 'sepa' ? 'EUR' : 'USD'

    const amount = Number(withdrawalEvent?.withdrawalAmount || '0')
    const fiatStartAmount = Math.round(((amount) / (1 - Number(fiat?.percentFee || '0'))) + Number(fiat?.flatFee || '0'))
    return {
      transactionHash,
      networkFeeAmount,
      fiat: {
        transferMethod: fiat?.transferMethod || 'Fiat',
        currencyOut: currency,
        fee: fiatStartAmount - amount,
        receiptAccountId: fiat?.withdrawalAccountId,
        finalAmount: fiat?.finalAmount
      }
    }
  }, [withdrawalEvent])

  const logo = useMemo(() => fiat.currencyOut === 'EUR' ? EUROLogo : USDLogo, [fiat])

  const finalAmountCrypto = useMemo(() => {
    if (!withdrawalEvent || !networkFeeAmount) return
    if (!isFiatWithdrawal) {
      return trimZeros((Number(withdrawalEvent.withdrawalAmount) - Number(networkFeeAmount)).toString())
    }
  }, [isFiatWithdrawal, withdrawalEvent, networkFeeAmount])

  const fetchWithdrawalData = async (id: string): Promise<void> => {
    setIsLoading(true)
    let withdrawal: WithdrawalRequest
    let isFiat: boolean
    try {
      setReceiptAccount(undefined)
      setWithdrawalDestination(undefined)
      setManagedBalanceLedger(undefined)
      withdrawal = await Radom.getWithdrawal(id)
      isFiat = !!withdrawal.receipt?.FiatReceipt
      setWithdrawalEvent(withdrawal)
      setPaymentMethod(getMethod(withdrawal.network, withdrawal.token))
    } catch {
      setIsLoading(false)
      errorToast('Failed to load withdrawal')
      return
    }

    const accountId = withdrawal.withdrawalAccountId
    try {
      if (accountId) {
        const acc = await Radom.getWithdrawalAccount(accountId)
        setReceiptAccount(acc)
      } else {
        const accs = await Radom.listWithdrawalAccounts()
        const actualAccount =
            accs.find(acc =>
              acc.accountType === 'crypto' &&
              acc.accountDetails.Crypto?.network === withdrawal.network &&
              (acc.accountDetails.Crypto?.address || '').toLowerCase() === (withdrawal.withdrawalAddress || '').toLowerCase()
            )
        setReceiptAccount(actualAccount)
      }
    } catch {
      errorToast('Failed to load withdrawal account')
    }

    try {
      const ledger = await Radom.listManagedBalanceLedger([withdrawal.id])
      if (ledger.length > 0) { setManagedBalanceLedger(ledger[0]) }
    } catch {
      errorToast('Failed to load ledger')
    }

    if (isFiat) {
      try {
        const event = await Radom.getWithdrawalEvent(withdrawal.id)
        const data = event.eventData.withdrawal.destination
        setWithdrawalDestination(data)
      } catch {
        errorToast('Failed to load withdrawal event for fiat withdrawal')
      }
    }
    setIsLoading(false)
  }

  const debouncedFetchWithdrawalData = useMemo(() => _.debounce(fetchWithdrawalData, 300), [])

  useEffect(() => {
    if (!params.id) return
    debouncedFetchWithdrawalData(params.id)
    return () => debouncedFetchWithdrawalData.cancel()
  }, [params.id])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (withdrawalEvent) {
    return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: withdrawalEvent.id, to: `/withdrawal/${withdrawalEvent.id}?source=transactions` }
            ]
            : [
              { item: 'Withdrawals', to: '/withdrawals' },
              { item: withdrawalEvent.id, to: '.' }
            ]
        } />
      </div>
      <Section>
        <div style={{
          fontSize: '18px',
          paddingBottom: 5,
          display: 'flex',
          flexDirection: 'row',
          gap: 10,
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '32px'
          }}>
            {paymentMethod &&
              <img src={paymentMethod.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
            }
            {isFiatWithdrawal
              ? <span>{trimZeros((Number(withdrawalEvent.withdrawalAmount) + fiat.fee).toString())}</span>
              : <span>{trimZeros(withdrawalEvent.withdrawalAmount)}</span>
            }
            {paymentMethod &&
              <span style={{
                background: RADOM_COLORS.GRAY_MED,
                color: RADOM_COLORS.BLACK,
                borderRadius: 10,
                fontSize: 14,
                padding: '5px 10px',
                fontWeight: 400
              }}>{paymentMethod.ticker}</span>
            }
          </div>
          {isFiatWithdrawal && withdrawalEvent.withdrawalStatus !== 'pending' && <>
            <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 16, height: 16, transform: 'rotate(-90deg)' }} />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '32px'
            }}>
              <img src={logo as string} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
              <span>{trimZeros(fiat?.finalAmount ?? '0')}</span>
              <span style={{
                background: RADOM_COLORS.GRAY_MED,
                color: RADOM_COLORS.BLACK,
                borderRadius: 10,
                fontSize: 14,
                padding: '5px 10px',
                fontWeight: 400
              }}>{fiat.currencyOut}</span>
            </div></>}
        </div>

        <SectionContent>
          {withdrawalEvent.withdrawalStatus === 'complete' && <SectionItem>
            <SubTitle>Type</SubTitle>
            <span>{isFiatWithdrawal ? 'Fiat Withdrawal' : 'Crypto Withdrawal'}</span>
          </SectionItem>}
          <SectionItem>
            <SubTitle>Status</SubTitle>
            <StatusBadge status={withdrawalEvent.withdrawalStatus}>{withdrawalEvent.withdrawalStatus}</StatusBadge>
          </SectionItem>
          <SectionItem>
            <SubTitle>Withdrawal request ID</SubTitle>
            <div style={{
              display: 'flex',
              gap: 4
            }}>
              <span>{withdrawalEvent.id}</span>
              <IconButton style={{ padding: 0 }}>
                <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard
                    .writeText(withdrawalEvent.id)
                    .then(() => {
                      successToast(
                        'Copied withdrawal event id'
                      )
                    })
                    .catch((err) => {
                      console.error('copy failed', err)
                      errorToast(
                        'Failed to copy withdrawal event id'
                      )
                    })
                }} />
              </IconButton>
            </div>
          </SectionItem>

        </SectionContent>
      </Section>
      {
        (managedBalanceLedger && paymentMethod &&
    !(isFiatWithdrawal && withdrawalEvent?.withdrawalStatus === 'pending')
        ) &&
              <Section>
                <SubTitle style={{ fontSize: 18 }}>
                      Ledger
                </SubTitle>
                <SectionContent>
                  <SectionItem>
                    <SubTitle>Balance Before</SubTitle>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                      <span>
                        {roundAssetAmount(
                          paymentMethod.ticker,
                          Number(managedBalanceLedger.balanceBefore)
                        )}
                      </span>
                      <span style={{ color: RADOM_COLORS.GRAY4 }}>
                        {paymentMethod.ticker ?? paymentMethod.logo} on {managedBalanceLedger.network}
                      </span>
                    </div>
                  </SectionItem>
                  <SectionItem>
                    <SubTitle>Balance After</SubTitle>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                      <span>
                        {roundAssetAmount(
                          paymentMethod.ticker,
                          Number(managedBalanceLedger.balanceAfter)
                        )}
                      </span>
                      <span style={{ color: RADOM_COLORS.GRAY4 }}>
                        {paymentMethod.ticker ?? paymentMethod.logo} on {managedBalanceLedger.network}
                      </span>
                    </div>
                  </SectionItem>
                </SectionContent>
              </Section>
      }
      <DestinationDetails
        isFiatWithdrawal={isFiatWithdrawal}
        addressTo={withdrawalEvent.withdrawalAddress}
        transferMethod={withdrawalEvent.withdrawalStatus === 'complete'
          ? isFiatWithdrawal && fiat ? fiat.transferMethod : 'Crypto'
          : undefined}
        accountName={receiptAccount?.accountType === 'crypto' || isFiatWithdrawal ? accountName : undefined} routingNumber={routingNumber} accountNumber={accountNumber}
        accountId={receiptAccount?.accountType === 'crypto' || isFiatWithdrawal ? accountId : undefined}
        withdrawalDestination={withdrawalDestination}
      />
      {withdrawalEvent.withdrawalStatus === 'complete' && paymentMethod &&
        <WithdrawalDetails paymentMethod={paymentMethod} requestedAt={withdrawalEvent.requestedAt} completedAt={
          withdrawalEvent.completedAt} transactionHash={transactionHash} network={withdrawalEvent.network}
        networkFeeAmount={networkFeeAmount} finalAmountCrypto={finalAmountCrypto}
        amount={withdrawalEvent.withdrawalAmount} isFiatWithdrawal={isFiatWithdrawal} fiat={fiat}
        logo={logo as string}/>
      }
    </Container>
  }

  return <div>
    <span>Withdrawal not found</span>
  </div>
})

export default Withdrawal
